import styled from '@emotion/styled';
import { Button, DatePicker, Input, Select, Table } from 'antd';
import { orderBy, sumBy } from 'lodash';
import moment, { Moment } from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FiBook, FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { GrandLivreEntity } from '../../../../entities/grandLivre.entity';
import { ROUTES } from '../../../../routes';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import { Layout } from '../../../shared/Layout';
import { exportGLData } from '../components/exportDataExcel';
import { getGrandLivre } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 90% 8%, rgba(255, 176, 0, 0.08), transparent 20%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    color: var(--mt-text);
  }

  .ledger-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .ledger-hero h1 {
    margin: 5px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .ledger-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.5;
  }

  .hero-actions,
  .page-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ghost-action,
  .primary-action {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .kpi-card strong {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 21px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(280px, 420px) 1fr;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 12px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 36px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(130px, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .filter-grid .ant-select-selector,
  .filter-grid .ant-picker {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 8px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-item,
  .filter-grid .ant-picker-input input {
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-placeholder,
  .filter-grid .ant-picker-input input::placeholder {
    color: var(--mt-muted) !important;
  }

  .table-shell {
    margin-top: 12px;
    overflow: hidden;
  }

  .table-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: #fff7e8;
    font-size: 11px;
    text-transform: none;
  }

  .ledger-table .ant-table {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .ledger-table .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    font-size: 11px;
    text-transform: uppercase;
  }

  .ledger-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .account-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .account-cell strong,
  .piece-cell strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .account-cell span,
  .piece-cell span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .piece-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 220px;
  }

  .amount-cell {
    display: block;
    text-align: right;
    font-family: Poppins-B;
    white-space: nowrap;
  }

  .amount-cell.credit {
    color: #00965d;
  }

  .amount-cell.debit {
    color: #dc2626;
  }

  .amount-cell.balance {
    color: var(--mt-warning);
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .ledger-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .filter-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) => (
  <div className='kpi-card'>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
    <div className='icon'>{icon}</div>
  </div>
);

export const GrandLivrePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [grandLivre, setGrandLivre] = useState<GrandLivreEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const history = useHistory();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    setIsLoading(true);
    getGrandLivre(connectedUser.id).then(({ data }) => {
      setGrandLivre(orderBy(data, 'created_at', 'desc'));
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredGrandLivre = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return grandLivre.filter((line) => {
      const searchable = [
        line.num_compte,
        line.intitule_compte,
        line.document,
        line.piece,
        line.libelle,
        line.categorie_titre,
        line.tresor_emetteur,
        line.titulaire,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const documentDate = moment(line.date_document || line.date_saisi || line.created_at);
      const matchesSearch = !term || searchable.includes(term);
      const matchesType = typeFilter === 'all' || line.type === typeFilter;
      const matchesActive =
        activeFilter === 'all' ||
        (activeFilter === 'active' && isActiveStatus(line.actif)) ||
        (activeFilter === 'inactive' && !isActiveStatus(line.actif));
      const matchesCategory =
        categoryFilter === 'all' || line.categorie_titre === categoryFilter;
      const matchesDate =
        !dateRange ||
        (documentDate.isValid() &&
          documentDate.isSameOrAfter(dateRange[0], 'day') &&
          documentDate.isSameOrBefore(dateRange[1], 'day'));

      return (
        matchesSearch &&
        matchesType &&
        matchesActive &&
        matchesCategory &&
        matchesDate
      );
    });
  }, [activeFilter, categoryFilter, dateRange, grandLivre, searchTerm, typeFilter]);

  const filterOptions = useMemo(
    () => ({
      categories: uniqueOptions(grandLivre.map((line) => line.categorie_titre)),
      types: uniqueOptions(grandLivre.map((line) => line.type)),
    }),
    [grandLivre],
  );

  const ledgerStats = useMemo(
    () => ({
      credit: sumBy(filteredGrandLivre, 'credit'),
      debit: sumBy(filteredGrandLivre, 'debit'),
      lines: filteredGrandLivre.length,
      solde: sumBy(filteredGrandLivre, 'solde'),
    }),
    [filteredGrandLivre],
  );

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <section className='ledger-hero'>
            <div>
              <span className='eyebrow'>Comptabilité titres</span>
              <h1>Grand livre</h1>
              <p>
                Consultez les écritures comptables détaillées, les pièces, les
                comptes et les mouvements débit/crédit liés aux opérations sur titres.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <Button
                icon={<FiBook style={{ marginBottom: -2, marginRight: 5 }} />}
                className='ghost-action'
                onClick={() => history.push(ROUTES.PANEL.COMPTABILITE.BALANCE_G)}
              >
                Balance générale
              </Button>
              <ExportFormatButton
                className='primary-action'
                onExport={(format) => exportGLData(filteredGrandLivre, format, getExportMetadata(connectedUser))}
              />
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard label='Écritures' value={ledgerStats.lines || '--'} icon={<FiBook />} />
            <KpiCard
              label='Cumul crédit'
              value={`${formatNum(ledgerStats.credit) || '--'} XAF`}
              icon={<FiBook />}
            />
            <KpiCard
              label='Cumul débit'
              value={`${formatNum(ledgerStats.debit) || '--'} XAF`}
              icon={<FiBook />}
            />
            <KpiCard
              label='Solde net'
              value={`${formatNum(ledgerStats.solde) || '--'} XAF`}
              icon={<FiBook />}
            />
          </section>

          <section className='toolbar'>
            <div>
              <div className='search-box'>
                <FiSearch />
                <Input
                  placeholder='Rechercher compte, pièce, titre, titulaire...'
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className='filter-grid'>
                <Select value={typeFilter} onChange={setTypeFilter}>
                  <Select.Option value='all'>Tous types</Select.Option>
                  {filterOptions.types.map((type) => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
                <Select value={activeFilter} onChange={setActiveFilter}>
                  <Select.Option value='all'>Toutes écritures</Select.Option>
                  <Select.Option value='active'>Actives</Select.Option>
                  <Select.Option value='inactive'>Inactives</Select.Option>
                </Select>
                <Select value={categoryFilter} onChange={setCategoryFilter}>
                  <Select.Option value='all'>Toutes catégories</Select.Option>
                  {filterOptions.categories.map((category) => (
                    <Select.Option key={category} value={category}>
                      {category}
                    </Select.Option>
                  ))}
                </Select>
                <DatePicker.RangePicker
                  value={dateRange as any}
                  onChange={(value) => setDateRange(value as [Moment, Moment] | null)}
                  format='DD/MM/YYYY'
                />
              </div>
            </div>
            <div className='page-toolbar'>
              <span className='table-count'>{filteredGrandLivre.length} lignes affichées</span>
            </div>
          </section>

          <section className='table-shell'>
            <div className='table-title'>
              <span>Écritures comptables</span>
              <span className='table-count'>{grandLivre.length} lignes total</span>
            </div>
            <Table
              columns={COLUMNS}
              dataSource={filteredGrandLivre}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              className='ledger-table'
              size='small'
              loading={isLoading}
              pagination={{
                defaultPageSize: 50,
                position: ['bottomRight'],
                showSizeChanger: true,
                pageSizeOptions: ['50', '100', '200', '250'],
              }}
              scroll={{ x: 'max-content' }}
            />
          </section>
        </div>
      </Container>
    </Layout>
  );
};

const COLUMNS = [
  {
    title: 'Compte',
    width: 260,
    render: (_cell: any, row: GrandLivreEntity) => (
      <div className='account-cell'>
        <strong>{row.num_compte || '--'}</strong>
        <span>{row.intitule_compte || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Date saisi',
    dataIndex: 'date_saisi',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date document',
    dataIndex: 'date_document',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Pièce / Document',
    render: (_cell: any, row: GrandLivreEntity) => (
      <div className='piece-cell'>
        <strong>{row.piece || '--'}</strong>
        <span>{row.document || '--'}</span>
      </div>
    ),
  },
  {
    title: 'Libellé',
    dataIndex: 'libelle',
  },
  {
    title: 'Catégorie Titre',
    dataIndex: 'categorie_titre',
  },
  {
    title: 'Trésor emetteur',
    dataIndex: 'tresor_emetteur',
  },
  {
    title: 'Titulaire',
    dataIndex: 'titulaire',
  },
  {
    title: 'Crédit',
    dataIndex: 'credit',
    render: (cell: any) => (
      <span className='amount-cell credit'>{formatNum(cell) || '--'}</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'Débit',
    dataIndex: 'debit',
    render: (cell: any) => (
      <span className='amount-cell debit'>{formatNum(cell) || '--'}</span>
    ),
    align: 'right' as const,
  },
  {
    title: 'Solde',
    dataIndex: 'solde',
    render: (cell: any) => (
      <span className='amount-cell balance'>{formatNum(cell) || '--'}</span>
    ),
    align: 'right' as const,
  },
];

const isActiveStatus = (status?: string) => {
  const normalized = `${status || ''}`.toLowerCase();

  return ['o', 'oui', 'actif', 'active', '1', 'true'].includes(normalized);
};

const uniqueOptions = (values: Array<string | undefined>) =>
  Array.from(new Set(values.map((value) => `${value || ''}`.trim()).filter(Boolean)));
